import { useMemo, useState, type ReactNode } from "react";

import { useT } from "../i18n";
import { useTelaEstreita } from "./tela";

/**
 * Tabela que ordena, filtra e cabe num celular.
 *
 * O elenco tem nove colunas. Nenhuma escolha de tamanho faz nove colunas
 * caberem em 360 pixels, então num celular a tabela sempre foi arrastada de
 * lado, e quem arrastava perdia de vista de quem era a linha.
 *
 * A saída é escolher uma coluna por vez. Num celular a tabela mostra o que
 * identifica a linha (número, posição, nome) mais **uma** coluna de dado, e a
 * pessoa troca essa coluna num toque. No computador continuam as nove.
 *
 * O mesmo controle que troca a coluna também ordena por ela, porque são a
 * mesma pergunta feita duas vezes: quem quer ver a força quer ver quem tem
 * mais força. No computador a ordenação é o cabeçalho, que é onde se procura.
 *
 * Quem chama continua dono das linhas. Este módulo não busca dado nem decide
 * agrupamento: ele recebe as colunas, devolve `preparar`, e o elenco aplica
 * isso dentro de cada setor para os grupos não se desfazerem na ordenação.
 */

/** Onde a coluna aparece. */
export type Presenca =
  /** Nas duas larguras: é o que identifica a linha. */
  | "sempre"
  /** No computador sempre; no celular só quando é a escolhida. */
  | "destaque"
  /** Só no computador, e fora da lista de escolha. */
  | "desktop";

export type Direcao = "asc" | "desc";

export interface Coluna<T> {
  chave: string;
  rotulo: string;
  presenca: Presenca;
  largura?: number;
  /** Números vão para a direita, texto para a esquerda. */
  direita?: boolean;
  /** O valor que ordena. Sem isto a coluna não é ordenável. */
  ordenar?: (linha: T) => number | string;
  /**
   * Para que lado o primeiro clique ordena.
   *
   * O padrão é do maior para o menor, que é o que se quer de número: quem
   * ordena por força quer ver o melhor primeiro. Coluna de texto declara
   * `asc`, porque de nome se espera o alfabeto.
   */
  direcaoInicial?: Direcao;
  celula: (linha: T) => ReactNode;
}

export interface Ordem {
  chave: string;
  direcao: Direcao;
}

function comparar(a: number | string, b: number | string, direcao: Direcao): number {
  // Texto compara pelo idioma, senão "Ângelo" cai depois de "Zico" em qualquer
  // lista com acento, que é toda lista em português.
  const bruto =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true });
  return direcao === "asc" ? bruto : -bruto;
}

export interface Tabela<T> {
  /** As colunas que esta largura mostra, na ordem. */
  visiveis: Array<Coluna<T>>;
  ordem: Ordem | null;
  /** Clicar na mesma coluna inverte; clicar noutra começa pela mais alta. */
  alternar: (chave: string) => void;
  busca: string;
  definirBusca: (valor: string) => void;
  destaque: string | null;
  definirDestaque: (chave: string) => void;
  estreita: boolean;
  /** Se esta tabela tem busca própria. Quem já tem a sua não ganha outra. */
  temBusca: boolean;
  /** As colunas que o celular oferece como destaque. */
  opcoesDeDestaque: Array<Coluna<T>>;
  /** Filtra pela busca e ordena. Aplique por grupo para não desfazer grupos. */
  preparar: (linhas: T[]) => T[];
}

export function useTabela<T>({
  colunas,
  procurar,
  ordemInicial,
  destaqueInicial,
}: {
  colunas: Array<Coluna<T>>;
  /** O que a busca enxerga em cada linha. Sem isto não há campo de busca. */
  procurar?: (linha: T) => string;
  ordemInicial?: Ordem;
  /**
   * Qual coluna o celular mostra antes de a pessoa escolher.
   *
   * Sem isto vale a primeira de destaque, que é a ordem em que ela aparece no
   * computador, e essa ordem nem sempre é a da pergunta mais comum: no mercado
   * a primeira é o clube, e quem abre o mercado quer ver preço.
   */
  destaqueInicial?: string;
}): Tabela<T> {
  const estreita = useTelaEstreita();
  const [ordem, definirOrdem] = useState<Ordem | null>(ordemInicial ?? null);
  const [busca, definirBusca] = useState("");

  const opcoesDeDestaque = useMemo(
    () => colunas.filter((coluna) => coluna.presenca === "destaque"),
    [colunas],
  );

  const [destaque, definirDestaqueCru] = useState<string | null>(
    () => destaqueInicial ?? opcoesDeDestaque[0]?.chave ?? null,
  );

  const visiveis = useMemo(() => {
    if (!estreita) return colunas;
    return colunas.filter(
      (coluna) => coluna.presenca === "sempre" || coluna.chave === destaque,
    );
  }, [colunas, estreita, destaque]);

  function alternar(chave: string): void {
    const coluna = colunas.find((item) => item.chave === chave);
    if (!coluna?.ordenar) return;
    definirOrdem((anterior) =>
      anterior?.chave === chave
        ? { chave, direcao: anterior.direcao === "desc" ? "asc" : "desc" }
        : { chave, direcao: coluna.direcaoInicial ?? "desc" },
    );
  }

  /* Trocar a coluna em destaque já ordena por ela: é a mesma pergunta. */
  function definirDestaque(chave: string): void {
    definirDestaqueCru(chave);
    const coluna = colunas.find((item) => item.chave === chave);
    if (!coluna?.ordenar) return;
    definirOrdem((anterior) =>
      anterior?.chave === chave
        ? { chave, direcao: anterior.direcao === "desc" ? "asc" : "desc" }
        : { chave, direcao: coluna.direcaoInicial ?? "desc" },
    );
  }

  function preparar(linhas: T[]): T[] {
    const alvo = busca.trim().toLocaleLowerCase();
    const filtradas =
      alvo.length === 0 || !procurar
        ? linhas
        : linhas.filter((linha) => procurar(linha).toLocaleLowerCase().includes(alvo));

    if (!ordem) return filtradas;
    const coluna = colunas.find((item) => item.chave === ordem.chave);
    if (!coluna?.ordenar) return filtradas;
    const valor = coluna.ordenar;

    // Cópia antes de ordenar: `sort` mexe no próprio vetor, e o que chega aqui
    // costuma ser a lista que o estado do jogo guarda.
    return [...filtradas].sort((a, b) => comparar(valor(a), valor(b), ordem.direcao));
  }

  return {
    visiveis,
    ordem,
    alternar,
    busca,
    definirBusca,
    destaque,
    definirDestaque,
    estreita,
    temBusca: Boolean(procurar),
    opcoesDeDestaque,
    preparar,
  };
}

/**
 * Busca e escolha de coluna, acima da tabela.
 *
 * Os chips só existem no celular, porque no computador todas as colunas já
 * estão na tela e o cabeçalho é que ordena.
 */
export function ControlesDaTabela<T>({ tabela }: { tabela: Tabela<T> }): ReactNode {
  const t = useT();
  const temChips = tabela.estreita && tabela.opcoesDeDestaque.length > 1;
  if (!tabela.temBusca && !temChips) return null;

  return (
    <div
      className="grid"
      style={{ gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--border)" }}
    >
      {/* O mercado já tem busca por nome, com atalho de teclado e tudo. Uma
          segunda caixa ao lado da primeira seria duas buscas competindo pela
          mesma lista. */}
      {tabela.temBusca && (
        <input
          type="search"
          value={tabela.busca}
          onChange={(evento) => tabela.definirBusca(evento.target.value)}
          placeholder={t("tabela.buscar")}
          aria-label={t("tabela.buscar")}
          style={{ width: "100%" }}
        />
      )}

      {temChips && (
        /* Embrulha em vez de rolar: a queixa que gerou esta tela inteira era
           ter que arrastar, e trocar o arraste da tabela pelo arraste dos
           chips seria devolver o problema num lugar menor. */
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {tabela.opcoesDeDestaque.map((coluna) => {
            const ativa = coluna.chave === tabela.destaque;
            const ordenada = tabela.ordem?.chave === coluna.chave;
            return (
              <button
                key={coluna.chave}
                className="ghost fixo"
                onClick={() => tabela.definirDestaque(coluna.chave)}
                aria-pressed={ativa}
                style={{
                  minHeight: 30,
                  padding: "0 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  ...(ativa
                    ? { borderColor: "var(--primary)", color: "var(--primary)" }
                    : undefined),
                }}
              >
                {coluna.rotulo}
                {ativa && ordenada && (
                  <span aria-hidden style={{ marginLeft: 4 }}>
                    {tabela.ordem?.direcao === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * O cabeçalho, que ordena onde há o que clicar.
 *
 * Fica grudado no topo enquanto a lista rola. Numa lista de trinta jogadores a
 * pessoa chega no fim sem lembrar qual coluna era qual, que é a outra metade
 * de se perder na tabela.
 */
export function CabecalhoDaTabela<T>({ tabela }: { tabela: Tabela<T> }): ReactNode {
  const t = useT();

  return (
    <thead>
      <tr>
        {tabela.visiveis.map((coluna) => {
          const ordenavel = Boolean(coluna.ordenar);
          const ordenada = tabela.ordem?.chave === coluna.chave;
          return (
            <th
              key={coluna.chave}
              className={coluna.direita ? "right" : undefined}
              style={{
                width: coluna.largura,
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: "var(--surface)",
                cursor: ordenavel ? "pointer" : undefined,
                color: ordenada ? "var(--primary)" : undefined,
                whiteSpace: "nowrap",
              }}
              onClick={ordenavel ? () => tabela.alternar(coluna.chave) : undefined}
              aria-sort={
                ordenada
                  ? tabela.ordem?.direcao === "asc"
                    ? "ascending"
                    : "descending"
                  : undefined
              }
              title={ordenavel ? t("tabela.ordenarPor", { coluna: coluna.rotulo }) : undefined}
            >
              {coluna.rotulo}
              {ordenada && (
                <span aria-hidden style={{ marginLeft: 3 }}>
                  {tabela.ordem?.direcao === "asc" ? "↑" : "↓"}
                </span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

/** As células de uma linha, nas colunas que esta largura mostra. */
export function CelulasDaLinha<T>({
  tabela,
  linha,
}: {
  tabela: Tabela<T>;
  linha: T;
}): ReactNode {
  return (
    <>
      {tabela.visiveis.map((coluna) => (
        <td key={coluna.chave} className={coluna.direita ? "right" : undefined}>
          {coluna.celula(linha)}
        </td>
      ))}
    </>
  );
}

/** Linha de "nada encontrado", para a busca que não achou nada. */
export function LinhaVazia<T>({ tabela }: { tabela: Tabela<T> }): ReactNode {
  const t = useT();
  return (
    <tr>
      <td colSpan={tabela.visiveis.length} className="muted" style={{ padding: 18 }}>
        {t("tabela.nadaEncontrado")}
      </td>
    </tr>
  );
}
